/****************************************************************************
 * COPYRIGHT (c) 2025 Harman Connected Services
 *
 * All rights reserved
 *
 * This software embodies materials and concepts which are
 * confidential to Ignite Product and is made available solely
 * pursuant to the terms of a written license agreement with
 * Ignite Product.
 *
 * Designed and Developed by Harman International Industries, Inc.
 ***************************************************************************/

import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useLocation, useParams, useNavigate } from "react-router-dom";
import {
  Snackbar,
  Backdrop,
  CircularProgress,
  Breadcrumbs,
  Link,
  Container,
  Switch
} from "@mui/material";
import { fetchSubmissionDetails } from "../../../Services/redux/Application-details/ApplicationDetailsActions";
import "./appProduct.css";
import Topheader from "../../../Layout/header/header";
import ApplicationDetailsSideMenu from "../../../Components/ApplicationDetailsSideMenu/ApplicationDetailsSideMenu";
import ApplicationDetailsErrorDialog from "../../../Components/ApplicationDetailsErrorDialog/ApplicationDetailsErrorDialog";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import Button from "@mui/material/Button";
import Grid from "@mui/material/Grid2";
import { useForm } from "react-hook-form";
import TextField from "@mui/material/TextField";
import Select from "@mui/material/Select";
import MenuItem from "@mui/material/MenuItem";
import Divider from "@mui/material/Divider";
import InputAdornment from "@mui/material/InputAdornment";
import Stack from "@mui/material/Stack";
import APIManager from "../../../Services/ApiManager/ApiManager";
import { DataGrid, useGridApiContext } from "@mui/x-data-grid";
import Alert from "@mui/material/Alert";
import EditIcon from "@mui/icons-material/Edit";
import Typography from "@mui/material/Typography";
import PropTypes from "prop-types";

function RenderEditIcon({ id, unsavedChangesRef }) {
  const apiRef = useGridApiContext();

  return (
    <>
      {unsavedChangesRef.current.unsavedRows !== id && (
        <div>
          <strong>
            <EditIcon
              className="edit-icon"
              onClick={() => {
                apiRef.current.startCellEditMode({
                  id: id,
                  field: "price",
                });
                unsavedChangesRef.current.unsavedRows = id;
              }}
            />
          </strong>
        </div>
      )}
    </>
  );
}

RenderEditIcon.propTypes = {
  id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
  unsavedChangesRef: PropTypes.shape({
    current: PropTypes.object.isRequired,
  }).isRequired,
};

function AppProduct(props) {
  const { appId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const teamId = location.state.teamId;
  const sellerId = location.state.sellerId;
  const [packageName, setPackageName] = useState("");
  const [exchangePriceData, setExchangePriceData] = useState([]);
  const [openSnackbar, setOpenSnackbar] = useState(false);
  const [snackMsg, setSnackMsg] = useState("");
  const [descriptionLength, setDescriptionLength] = useState(0);
  const [disableEnableRecurrence, setDisableEnableRecurrence] = useState(true);
  const { loading, submissionDetails } = useSelector(
    (state) => state.submissionDetails
  );
  const appData = useSelector(
    (state) => state.applicationDetails.applicationDetails
  );
  const handleChangeDescription = (event) => {
    setDescriptionLength(event.target.value.length);
    checkRecurringAppEnable();
  };
  const {
    register,
    formState: { errors, isValid },
    handleSubmit,
    getValues,
  } = useForm({
    mode: "onTouched",
  });

  const errorsApp = useSelector((state) => state.errors);
  const dispatch = useDispatch();
  const [loader, setLoader] = useState(false);
  const [countryTableRows, setCountryTableRows] = useState([]);

  // Helper function to create price object
  const createPriceObject = (formData, obj, isBasePrice = false) => ({
    isTaxable: false,
    periodUnit: "DAY",
    price: isBasePrice ? obj.price : Number(obj.price).toFixed(2),
    status: "ACTIVE",
    currencyCode: obj.currencyCode,
    countryCode: obj.countryCode,
    country: obj.country,
    ...(isBasePrice && { basePrice: true }),
    taxRateinPercent: obj.taxRateinPercent,
    taxRate: obj.taxRate,
    taxIncluded: false,
    name: formData.productId + "_" + obj.countryCode,
    description: formData.productId + "_" + obj.countryCode,
  });

  // Helper function for common text field validation patterns
  const getTextFieldValidation = (fieldName, maxLength = 44) => ({
    required: `${fieldName} is required`,
    maxLength: {
      value: maxLength,
      message: `${fieldName} cannot exceed ${maxLength} characters`
    },
    ...(maxLength === 44 && {
      pattern: {
        value: /^[a-zA-Z0-9\-_.]+$/,
        message: `${fieldName} can only contain alphanumeric characters, hyphens, underscores, and periods`
      }
    }),
    onChange: () => {
      checkRecurringAppEnable();
    },
  });

  // Helper function for Backdrop component
  const renderBackdrop = (open) => (
    <Backdrop
      sx={(theme) => ({ color: "#fff", zIndex: theme.zIndex.drawer + 1 })}
      open={open}
    >
      <CircularProgress color="inherit" />
    </Backdrop>
  );

  useEffect(() => {
    if (appData?.submissions?.length > 0) {
      dispatch(fetchSubmissionDetails(appId, appData.submissions[0].id));
    }
  }, [appData]);
  useEffect(() => {
    if (submissionDetails?.binary?.packageName) {
      setPackageName(submissionDetails.binary.packageName);
    }
  }, []);

  const backToApplications = () => {
    navigate(-1);
  };

  const backToAppDetails = () => {
    navigate(`/ui/developer/applications/${appId}`, {
      state: { teamId: teamId, sellerId: sellerId },
    });
  };

  const callNewSubmissionId = (submissionId) => {
    dispatch(fetchSubmissionDetails(appId, submissionId));
  };
  const populatePrice = (event) => {
    setLoader(true);
    let basePrice = event.target.value;
    if (!Number.isNaN(Number.parseFloat(basePrice)) && Number.parseFloat(basePrice) != 0) {
      APIManager.getExchangeRates(basePrice, "USD").then((json) => {
        if (json.currencyData && json.currencyData.length > 0) {
          setExchangePriceData(json.currencyData);
          createDataGrid(json.currencyData);
          setLoader(false);
        }
      });
    }
    if (basePrice === "") {
      setExchangePriceData([]);
      setCountryTableRows([]);
    }
    setLoader(false);
  };
  const createDataGrid = (data) => {
    let countryData = [];
    let count = 1;
    data.forEach((country) => {
      countryData.push({
        id: count,
        country: country.country,
        currencyCode: country.currencyCode,
        price: country.price,
        taxRate:
          country.taxRateinPercent +
          " (" +
          country.currencyCode +
          " " +
          country.taxRate +
          ")",
      });
      count++;
    });
    setCountryTableRows(countryData);
  };
  const countryTableColumns = [
    {
      field: "country",
      headerName: "Country",
      width: 140,
      headerClassName: "country-table-general-header",
      renderCell: (params) => (
        <div style={{ paddingLeft: "8px" }}>
          {params.value}
        </div>
      ),
    },
    {
      field: "currencyCode",
      headerName: "Price",
      align: "right",
      headerAlign: "right",
      sortable: false,
      headerClassName: "country-table-general-header",
      renderCell: (params) => (
        <span style={{ color: "#717273" }}>{params.value}</span>
      ),
    },
    {
      field: "price",
      headerName: "(Editable)",
      editable: true,
      align: "left",
      headerAlign: "left",
      type: "number",
      width: 80,
      headerClassName: "price-colum-header",
    },
    {
      field: "editTax",
      headerName: "",
      align: "left",
      renderCell: (params) => (
        <RenderEditIcon id={params.id} unsavedChangesRef={unsavedChangesRef} />
      ),
      sortable: false,
      headerClassName: "country-table-general-header",
    },
    {
      field: "taxRate",
      headerName: "Tax Rate",
      width: 140,
      align: "left",
      headerAlign: "left",
      headerClassName: "country-table-general-header",
    },
  ];

  const onSubmit = (data) => {
    setLoader(true);
    const requestPayload = buildProductPayload(data, data.basePrice);
    saveProduct(requestPayload);
  };

  const buildProductPayload = (formData, basePrice) => {
    const product = {
      productId: formData.productId,
      name: formData.name,
      description: formData.description,
      status: "ACTIVE",
      productType: "INAPP",
      resourceType: "NONCONSUMABLE",
      sellerId: sellerId,
      packageId: packageName,
      applicationId: appId,
    };

    const usObject = exchangePriceData.find(
      (item) => item.countryCode === "USA"
    );

    let trialPrice = null;
    if (formData.trialPeriod && formData.trialPeriod !== 0) {
      trialPrice = {
        status: "ACTIVE",
        trialPeriod: formData.trialPeriod,
        trialPeriodUnit: "DAY",
        trialFlag: true,
        name: formData.productId + "_trial",
        description: formData.productId + "_trial",
      };
    }

    const pricingWithoutUSObj = exchangePriceData.filter(
      (item) => item.countryCode !== "USA"
    );
    
    const planPrices = pricingWithoutUSObj.map((obj) => 
      createPriceObject(formData, obj)
    );

    const basePriceObj = createPriceObject(
      formData, 
      { ...usObject, price: basePrice }, 
      true
    );

    const oneTimePlan = {
      planType: "ONETIME",
      name: formData.productId,
      planPrices: planPrices,
      basePrice: basePriceObj,
    };

    return {
      product: product,
      trialPrice: trialPrice,
      oneTimePlan: oneTimePlan,
    };
  };

  const saveRecApp = () => {
    setLoader(true);
    const product = {
      productId: getValues("productId"),
      name: getValues("name"),
      description: getValues("description"),
      status: "ACTIVE",
      productType: "INAPP",
      resourceType: "NONCONSUMABLE",
      sellerId: sellerId,
      packageId: packageName,
      applicationId: appId,
    };

    let trialPrice = null;
    if (getValues("trialPeriod") !== 0) {
      trialPrice = {
        status: "ACTIVE",
        trialPeriod: getValues("trialPeriod"),
        trialPeriodUnit: "DAY",
        trialFlag: true,
        name: getValues("productId") + "_trial",
        description: getValues("productId") + "_trial",
      };
    }
    let requestPayload = {
      product: product,
      trialPrice: trialPrice,
    };
    recerrenceSaveInApp(requestPayload);
  };

  const recerrenceSaveInApp = (data) => {
    APIManager.saveInAppWithEnableRecerence(data).then((json) => {
      setOpenSnackbar(true);
      setSnackMsg(`Added successfully`);
      setTimeout(function () {
        navigate(
          `/ui/developer/applications/${appId}/edit-recurring-product/${json.product.id}`,
          {
            state: { teamId: teamId, sellerId: sellerId },
          }
        );
      }, 3000);
    });
  };

  const saveProduct = (data) => {
    APIManager.saveInApp(data).then((json) => {
      setOpenSnackbar(true);
      setSnackMsg(`Added successfully`);
      setTimeout(function () {
        navigate(-1);
      }, 3000);
    });
  };

  const unsavedChangesRef = React.useRef({
    unsavedRows: "",
  });

  const processRowUpdate = (newRow) => {
    const updatedRow = { ...newRow, lastUpdated: new Date() };
    let countryObj = exchangePriceData.find(
      (item) => item.country === newRow.country
    );
    let changedPrice = newRow.price;
    let newTaxRate = newRow.price * (countryObj.taxRateinPercent / 100);
    updatedRow.taxRate =
      countryObj.taxRateinPercent +
      " (" +
      countryObj.currencyCode +
      " " +
      newTaxRate.toFixed(2) +
      ")";
    updatedRow.price = Number(changedPrice).toFixed(2);
    exchangePriceData.find(
      (item) => item.country === updatedRow.country
    ).price = updatedRow.price;
    exchangePriceData.find(
      (item) => item.country === updatedRow.country
    ).taxRate = newTaxRate.toFixed(2);
    unsavedChangesRef.current.unsavedRows = "";
    return updatedRow;
  };

  const checkRecurringAppEnable = () => {
    if (
      getValues("productId") !== "" &&
      getValues("name") !== "" &&
      getValues("description") !== ""
    ) {
      setDisableEnableRecurrence(false);
    } else {
      setDisableEnableRecurrence(true);
    }
  };

  const handleProcessRowUpdateError = (error) => {};

  return (
    <>
      {loader && renderBackdrop(loader)}

      <Topheader />
      {
        <Container maxWidth="lg">
                          <Grid container>
                            <Grid items xs={12} sm={12} md={12} lg={12}></Grid>
        <div
          className="container"
          style={
            submissionDetails.status === "DRAFT"
              ? {
                  display: "flex",
                  justifyContent: "center",
                  flexDirection: "column",
                  width: "1260px",
                }
              : { display: "flex", flexDirection: "row", width: "1260px" }
          }
        >
          <div
            className="side-menu"
            style={
              submissionDetails.status === "DRAFT"
                ? { marginTop: "0px" }
                : { marginTop: "69px" }
            }
          >
            <button 
              className="arrow-back" 
              style={{ cursor: "pointer", background: "none", border: "none", padding: 0 }}
              onClick={backToApplications}
              type="button"
            >
              <div>
                <ArrowBackIcon style={{ color: " #717273" }} />
              </div>
              <div className="my-apps">In App Products</div>
            </button>
            {submissionDetails &&
              Object.keys(submissionDetails).length > 0 &&
              submissionDetails.status !== "DRAFT" &&
              appData &&
              Object.keys(appData).length > 0 && (
                <ApplicationDetailsSideMenu
                  appData={appData}
                  teamId={teamId}
                  isMonetized={submissionDetails.isMonetized}
                  callNewSubmissionId={callNewSubmissionId}
                />
              )}
          </div>
          <div
            className="submission-container"
            style={
              submissionDetails.status === "DRAFT"
                ? { marginLeft: "0px" }
                : { width: "93%", marginTop: "12px", marginLeft: "70px" }
            }
          >
            <form onSubmit={handleSubmit(onSubmit)}>
              <div>
                <Breadcrumbs
                  aria-label="breadcrumb"
                  sx={{ marginLeft: "15%", marginTop: "32px" }}
                >
                  <Link
                    underline="hover"
                    onClick={backToAppDetails}
                    sx={{
                      fontWeight: "500",
                      color: "#2a2c2d",
                      cursor: "pointer",
                    }}
                  >
                    <ArrowBackIcon
                      sx={{ mr: 0.5, marginRight: "19px", color: "#b3b3b3" }}
                      fontSize="inherit"
                    />
                    {submissionDetails &&
                      Object.keys(submissionDetails).length > 0 && (
                        <>{submissionDetails.descriptions[0].applicationName}</>
                      )}
                  </Link>
                  <Link
                    underline="hover"
                    onClick={backToApplications}
                    sx={{
                      fontWeight: "500",
                      color: "#2a2c2d",
                      cursor: "pointer",
                    }}
                  >
                    In App Products
                  </Link>
                  <Link
                    underline="none"
                    sx={{ fontWeight: "500", color: "#2a2c2d" }}
                  >
                    Add New Product
                  </Link>
                </Breadcrumbs>
              </div>
              <div className="in-app-title" style={{ marginLeft: "15%" }}>
                <Grid container spacing={2}>
                  <Grid size={9}>
                    <h2>Add New Product</h2>
                  </Grid>
                  <Grid size={3}>
                    <Stack direction="row" spacing={2}>
                      <Button
                        variant="contained"
                        sx={(theme) => ({
                          backgroundColor: "#e8e8e8",
                          color: "black",
                          textTransform: "none",
                        })}
                        onClick={backToApplications}
                      >
                        Cancel
                      </Button>
                      <Button
                        variant="contained"
                        sx={{ textTransform: "none" }}
                        disabled={!isValid || countryTableRows.length === 0}
                        type="submit"
                      >
                        Save
                      </Button>
                    </Stack>
                  </Grid>
                </Grid>
              </div>
              <div className="products-container">
                <Grid container spacing={2}>
                  <Grid size={4}>Basic Information</Grid>
                  <Grid size={4}>
                    <div>
                      <label className="form-label required" htmlFor="productId-input">Product ID</label>
                      <TextField
                        {...register("productId", getTextFieldValidation("Product ID"))}
                        id="productId-input"
                        style={{ marginBottom: "0", marginLeft: "0" }}
                        fullWidth
                        placeholder="Product ID"
                        variant="outlined"
                      />

                      {errors.productId && (
                        <Alert severity="error">
                          {errors.productId.message}
                        </Alert>
                      )}
                    </div>
                  </Grid>
                  <Grid size={4}>
                    <div>
                      <label className="form-label required" htmlFor="name-input">Name</label>
                      <TextField
                        {...register("name", getTextFieldValidation("Name"))}
                        id="name-input"
                        style={{ marginBottom: "0", marginLeft: "0" }}
                        fullWidth
                        placeholder="Name"
                        variant="outlined"
                      />
                      {errors.name && (
                        <Alert severity="error">{errors.name.message}</Alert>
                      )}
                    </div>
                  </Grid>
                </Grid>
                <Grid container spacing={2} sx={{ marginTop: "10px" }}>
                  <Grid size={4}></Grid>
                  <Grid size={8}>
                    <div>
                      <label className="form-label required" htmlFor="description-input">Description</label>
                      <TextField
                        {...register("description", {
                          required: "Description is required",
                          onChange: (e) => {
                            handleChangeDescription(e);
                          },
                        })}
                        id="description-input"
                        className="additional-textarea-class"
                        style={{ marginBottom: "0", marginLeft: "0" }}
                        fullWidth
                        placeholder="Description"
                        variant="outlined"
                        multiline
                        slotProps={{ htmlInput: { maxLength: 255 } }}
                        helperText={`${descriptionLength}/255`}
                        rows={3}
                      />
                      {errors.description && (
                        <Alert severity="error">
                          {errors.description.message}
                        </Alert>
                      )}
                    </div>
                  </Grid>
                </Grid>
                <Grid container spacing={2}>
                  <Grid size={4}></Grid>
                  <Grid size={3}>
                    <div>
                      <label className="form-label" htmlFor="trialPeriod-input">Trial Period</label>
                      <TextField
                        {...register("trialPeriod")}
                        id="trialPeriod-input"
                        rows={4}
                        style={{ marginBottom: "0", marginLeft: "0" }}
                        fullWidth
                        placeholder="Trial Period"
                        variant="outlined"
                      />
                    </div>
                  </Grid>
                  <Grid size={1.5}>
                    <div>
                      <label className="form-label no-label" htmlFor="demo-simple-select" aria-label="Trial period unit"> </label>
                      <Select
                        {...register("trialPeriodDays")}
                        fullWidth
                        labelId="demo-simple-select-label"
                        id="demo-simple-select"
                      >
                        <MenuItem value="1" selected>
                          Days
                        </MenuItem>
                      </Select>
                    </div>
                  </Grid>
                </Grid>
              </div>
              <div className="products-container-wide">
                <Divider flexItem />
                <br />
                <Grid container spacing={2}>
                  <Grid size={3.5}>Recurrence & Price</Grid>
                  <Grid size={8.5}>
                    <div className="monetize-wrapper">
                      <div className="wrapper-diver">
                        <p
                          className="monetize-text"
                          style={{ fontWeight: "bold" }}
                        >
                          Enable Recurrence{" "}
                          <span style={{ color: "red" }}>*</span>
                          <br />
                          <span
                            className="monetize-text-small"
                            style={{ marginLeft: "0" }}
                          >
                            <b>Note: </b>
                            <span style={{ color: "#757575" }}>
                              This setting cannot be undone once this product is
                              published
                            </span>
                          </span>
                        </p>

                        <div>
                          <Switch
                            {...register("enableRecurrence", {
                              onChange: (e) => {
                                saveRecApp();
                              },
                            })}
                            disabled={disableEnableRecurrence}
                          />
                        </div>
                      </div>
                    </div>
                  </Grid>
                </Grid>
                <Grid container spacing={2}>
                  <Grid size={3.5}></Grid>
                  <Grid size={4}>
                    <div
                      className="price-table-container"
                      style={{ marginTop: "10%" }}
                    >
                      <label className="form-label required" htmlFor="basePrice-input">Base Price</label>
                      <TextField
                        {...register(
                          "basePrice",
                          {
                            onBlur: (e) => {
                              populatePrice(e);
                            },
                          },
                          { required: "Base Price is required." }
                        )}
                        id="basePrice-input"
                        type="number"
                        slotProps={{
                          input: {
                            endAdornment: (
                              <InputAdornment position="end">
                                USD
                              </InputAdornment>
                            ),
                          },
                        }}
                        style={{ marginBottom: "0", marginLeft: "0" }}
                        fullWidth
                        helperText={`Base price doesn’t include tax`}
                        variant="outlined"
                      />
                      {errors.basePrice && (
                        <Alert severity="error">
                          {errors.basePrice.message}
                        </Alert>
                      )}
                    </div>
                  </Grid>
                </Grid>
                <br />
                <Grid container spacing={2}>
                  <Grid size={3.5}></Grid>
                  <Grid size={8.5}>
                    {countryTableRows.length > 0 && (
                      <div style={{ width: "100%" }}>
                        <Typography
                          variant="subtitle2"
                          sx={{ fontWeight: "bolder" }}
                          gutterBottom
                        >
                          Country specific price setting{" "}
                          <span className="red-star">*</span>
                        </Typography>
                        <Typography
                          variant="caption"
                          gutterBottom
                          sx={{ display: "block", color: "#757575" }}
                        >
                          These prices can be manually changed as per how you
                          want to change
                        </Typography>
                        <DataGrid
                          rows={countryTableRows}
                          columns={countryTableColumns}
                          disableColumnMenu
                          processRowUpdate={processRowUpdate}
                          onProcessRowUpdateError={handleProcessRowUpdateError}
                          initialState={{
                            pagination: {
                              paginationModel: { page: 0, pageSize: 5 },
                            },
                          }}
                          pageSizeOptions={[5, 10, 25]}
                          rowHeight={34}
                          columnHeaderHeight={32}
                          sx={{
                            '& .MuiDataGrid-cell': {
                              padding: '2px 4px',
                              fontSize: '0.875rem',
                              lineHeight: '1.2',
                              display: 'flex',
                              alignItems: 'center',
                            },
                            '& .MuiDataGrid-columnHeader': {
                              padding: '2px 4px',
                              fontSize: '0.875rem',
                              fontWeight: 'bold',
                              display: 'flex',
                              alignItems: 'center',
                            },
                            '& .MuiDataGrid-columnHeader--sortable[data-field="country"]': {
                              paddingLeft: '12px',
                            },
                            '& .MuiDataGrid-row': {
                              minHeight: '34px !important',
                              maxHeight: '34px !important',
                            },
                          }}
                          localeText={{
                            MuiTablePagination: {
                              labelRowsPerPage: "Items per page:",
                            },
                          }}
                        />
                      </div>
                    )}
                  </Grid>
                </Grid>
              </div>
            </form>
          </div>
        </div>
        </Grid>
                        </Container>
      }
      {errorsApp?.errorMessage != null &&
        errorsApp?.errorType != null && <ApplicationDetailsErrorDialog />}
      {(appData.loading || loading) && renderBackdrop(loading)}
      <Snackbar
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
        open={openSnackbar}
        autoHideDuration={3000}
        onClose={() => setOpenSnackbar(false)}
        message={snackMsg}
      />
    </>
  );
}

export default AppProduct;
